import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Zap, Loader2, Linkedin, Facebook, Twitter, Instagram, CheckCircle2,
  MessageSquare, CalendarDays, ShieldCheck,
} from "lucide-react";

const PLAT_ICON = { twitter: Twitter, instagram: Instagram, linkedin: Linkedin, facebook: Facebook };

function CardComments({ token, card, defaultName }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(card.comments || []);
  const [name, setName] = useState(defaultName || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/portfolio/${token}/card/${card.id}/comment`, { author_name: name, note });
      setList((l) => [...l, r.data.comment]);
      setNote("");
      toast.success("Note sent to the team");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send note");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 border-t border-border pt-2">
      {list.length > 0 && (
        <div className="space-y-1.5 mb-2" data-testid={`card-comments-${card.id}`}>
          {list.map((c, i) => (
            <div key={i} className="text-[11px] rounded-md bg-secondary/40 px-2 py-1.5">
              <span className="font-medium text-foreground/90">{c.author_name}: </span>
              <span className="text-muted-foreground">{c.note}</span>
            </div>
          ))}
        </div>
      )}
      {open ? (
        <div className="space-y-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-8 text-xs bg-secondary/40" data-testid={`card-comment-name-${card.id}`} />
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note or change request for this post…" className="min-h-[60px] text-xs resize-none bg-secondary/40" data-testid={`card-comment-note-${card.id}`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={send} disabled={busy || !note.trim()} className="h-7 text-xs flex-1" data-testid={`card-comment-send-${card.id}`}>
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Send note"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="h-7 text-xs">Cancel</Button>
          </div>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-primary transition-colors" data-testid={`card-comment-toggle-${card.id}`}>
          <MessageSquare className="h-3 w-3" /> {list.length > 0 ? "Add another note" : "Leave a note on this post"}
        </button>
      )}
    </div>
  );
}

export default function ClientPortfolio() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // "approve" | "changes"

  const load = () => {
    setLoading(true);
    api.get(`/portfolio/${token}`)
      .then((r) => {
        setData(r.data);
        if (r.data.client_approved || r.data.client_decision) {
          setDone(r.data.client_decision || (r.data.client_approved ? "approve" : null));
          setName(r.data.approver_name || "");
          setNote(r.data.client_note || "");
        }
      })
      .catch((e) => setErr(e?.response?.data?.detail || "This shared campaign link is invalid or was revoked."))
      .finally(() => setLoading(false));
  };
  useEffect(load, [token]);

  const submit = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/portfolio/${token}/approve`, { approver_name: name, note, decision });
      setDone(decision);
      toast.success(decision === "approve" ? "Approval sent — thank you!" : "Feedback sent to the team");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6 text-center">
        <div>
          <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="h-7 w-7 text-muted-foreground" />
          </div>
          <h1 className="text-xl font-display font-bold">Link unavailable</h1>
          <p className="text-muted-foreground mt-2 max-w-sm">{err}</p>
        </div>
      </div>
    );
  }

  const accent = data.brand_accent || "#6366F1";
  const cards = data.cards || [];

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="client-portfolio">
      {/* Header */}
      <header className="border-b border-border sticky top-0 z-10 bg-background/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {data.brand_logo_url ? (
              <img src={data.brand_logo_url} alt="" className="h-10 w-10 rounded-lg object-contain bg-white/90 shrink-0" />
            ) : (
              <div className="h-10 w-10 rounded-lg grid place-items-center shrink-0 text-white font-display font-extrabold" style={{ background: accent }}>
                {(data.brand_name || "B")[0].toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-base font-display font-bold leading-tight truncate" data-testid="portfolio-brand">{data.brand_name}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                <CalendarDays className="h-3 w-3" /> {cards.length}-post campaign · for review
              </div>
            </div>
          </div>
          {done === "approve" ? (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40" data-testid="portfolio-status-approved"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approved</Badge>
          ) : done === "changes" ? (
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/40"><MessageSquare className="h-3.5 w-3.5 mr-1" /> Changes requested</Badge>
          ) : (
            <Badge variant="outline" className="border-border text-muted-foreground">Awaiting your review</Badge>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: accent }}>Content approval</p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1.5">Review this month's campaign</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Below is the full set of posts planned for {data.brand_name}. Scroll through, then approve or request changes at the bottom — no account needed.</p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((c, i) => {
            const PIcon = PLAT_ICON[c.platform] || Facebook;
            const imgUrl = c.image_path ? `${API}/media/${c.image_path}` : null;
            return (
              <div key={c.id || i} className="rounded-xl border border-border bg-card overflow-hidden flex flex-col" data-testid={`portfolio-card-${i}`}>
                <div className="flex items-center justify-between gap-2 px-3.5 py-2.5 border-b border-border">
                  <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-md border border-border bg-secondary text-muted-foreground">
                    {c.day_tag || (c.day ? `Day ${String(c.day).padStart(2, "0")}` : `Post ${i + 1}`)}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground capitalize"><PIcon className="h-3.5 w-3.5" /> {c.platform}</span>
                </div>
                <div className="relative aspect-[1200/630] bg-secondary/40 shrink-0" style={imgUrl ? undefined : { background: `radial-gradient(130% 130% at 80% -10%, ${accent} 0%, #16172e 48%, #0b0b16 100%)` }}>
                  {imgUrl ? (
                    <img src={imgUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="relative h-full flex flex-col justify-between p-4">
                      <span className="text-[8px] font-semibold tracking-[0.16em] uppercase px-2 py-1 rounded-full border border-white/30 bg-white/10 text-white self-start">{c.category_tag || "Post"}</span>
                      <div className="font-display text-base font-extrabold text-white leading-tight">{c.headline_text}</div>
                      <div className="h-1 w-10 rounded-full bg-white/90" />
                    </div>
                  )}
                </div>
                <div className="p-3.5 flex flex-col gap-2 flex-1">
                  <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap flex-1">{c.post_text}</p>
                  {c.hashtags?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {c.hashtags.map((h) => <span key={h} className="text-[11px] text-primary/80">{h}</span>)}
                    </div>
                  )}
                  <CardComments token={token} card={c} defaultName={name} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Approval panel */}
        <div className="mt-10 rounded-2xl border border-border bg-card p-6 max-w-2xl" data-testid="portfolio-approval">
          {done ? (
            <div className="text-center py-4">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-3" style={{ color: done === "approve" ? "#34d399" : "#fbbf24" }} />
              <h3 className="font-display text-lg font-bold">
                {done === "approve" ? "Thanks — this campaign is approved!" : "Your feedback has been sent"}
              </h3>
              <p className="text-muted-foreground mt-1.5 text-sm">
                {done === "approve"
                  ? "The team has been notified and will schedule the posts."
                  : "The team has been notified of your requested changes."}
                {name ? ` — ${name}` : ""}
              </p>
              {note && <p className="text-sm text-foreground/80 mt-3 italic">“{note}”</p>}
            </div>
          ) : (
            <>
              <h3 className="font-display text-lg font-bold">Approve this month</h3>
              <p className="text-sm text-muted-foreground mt-1">Add your name (and any notes), then approve or request changes.</p>
              <div className="mt-4 space-y-3">
                <Input data-testid="portfolio-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="bg-secondary/40" />
                <Textarea data-testid="portfolio-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional notes or change requests…" className="min-h-[90px] resize-none bg-secondary/40" />
              </div>
              <div className="flex flex-col sm:flex-row gap-2.5 mt-4">
                <Button onClick={() => submit("approve")} disabled={busy} data-testid="portfolio-approve-btn"
                  className="flex-1 text-white" style={{ background: accent }}>
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Approve campaign
                </Button>
                <Button onClick={() => submit("changes")} disabled={busy} variant="outline" data-testid="portfolio-changes-btn" className="flex-1 border-border">
                  <MessageSquare className="h-4 w-4 mr-2" /> Request changes
                </Button>
              </div>
            </>
          )}
        </div>

        <div className="mt-10 flex items-center gap-2 text-xs text-muted-foreground">
          <Zap className="h-3.5 w-3.5 text-primary" /> Powered by AutoGridSocial
        </div>
      </main>
    </div>
  );
}
